import { storableError } from '../../util/errors';

import {
  getProfileByUserId,
  updateProfile
} from '../../util/api';

import { uploadFiles } from '../../ducks/applications.duck';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileExtendedSettingsPage/CLEAR_UPDATED_FORM';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileExtendedSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileExtendedSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileExtendedSettingsPage/UPDATE_PROFILE_ERROR';

export const FETCH_EXTENDED_PROFILE_REQUEST = 'app/ProfileExtendedSettingsPage/FETCH_EXTENDED_PROFILE_REQUEST';
export const FETCH_EXTENDED_PROFILE_SUCCESS = 'app/ProfileExtendedSettingsPage/FETCH_EXTENDED_PROFILE_SUCCESS';
export const FETCH_EXTENDED_PROFILE_ERROR = 'app/ProfileExtendedSettingsPage/FETCH_EXTENDED_PROFILE_ERROR';


// ================ Reducer ================ //

const initialState = {
  updateInProgress: false,
  updateProfileError: null,
  extendedProfileData: null,
  fetchExtendedProfileInProgress: false,
  fetchExtendedProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case FETCH_EXTENDED_PROFILE_REQUEST:
      return {
        ...state,
        fetchExtendedProfileInProgress: true,
        fetchExtendedProfileError: null,
      };
    case FETCH_EXTENDED_PROFILE_SUCCESS:
      return {
        ...state,
        extendedProfileData: payload,
        fetchExtendedProfileInProgress: false,
      };
    case FETCH_EXTENDED_PROFILE_ERROR:
      return {
        ...state,
        extendedProfileData: null,
        fetchExtendedProfileInProgress: false,
        fetchExtendedProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const fetchExtendedDataRequest = params => ({
  type: FETCH_EXTENDED_PROFILE_REQUEST,
  payload: { params },
});
export const fetchExtendedDataSuccess = result => ({
  type: FETCH_EXTENDED_PROFILE_SUCCESS,
  payload: result,
});
export const fetchExtendedDataError = error => ({
  type: FETCH_EXTENDED_PROFILE_ERROR,
  payload: error,
  error: true,
});


// ================ Thunk ================ //

export const doUpdateProfile = actionPayload => {
  return async (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const state = getState();
    const providerId = state.ProfileExtendedSettingsPage?.extendedProfileData?.items?.[0]?.id;

    if (!providerId) {
      const error = new Error("Provider ID not found");
      dispatch(updateProfileError(error));
      return Promise.reject(error);
    }

    try {
      // 1. Upload primary photo if provided (to both S3 and Sharetribe)
      let primaryPhotoUrl = null;
      let sharetribeImageId = null;
      if (actionPayload.primaryPhoto && actionPayload.primaryPhoto instanceof File) {
        // Upload to S3 first
        const uploaded = await dispatch(uploadFiles([actionPayload.primaryPhoto], null, true));
        primaryPhotoUrl = uploaded[0]?.url || uploaded[0]?.key || uploaded[0];
        
        // Also upload to Sharetribe
        try {
          const bodyParams = {
            image: actionPayload.primaryPhoto,
          };
          const queryParams = {
            expand: true,
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
          };
          
          const sharetribeResponse = await sdk.images.upload(bodyParams, queryParams);
          sharetribeImageId = sharetribeResponse.data.data.id.uuid;
        } catch (error) {
          console.error('Failed to upload to Sharetribe:', error);
          // Don't fail if Sharetribe upload fails
        }
      }

      // 2. Upload spouse photo if provided
      let spousePhotoUrl = null;
      if (actionPayload.spousePhoto && actionPayload.spousePhoto instanceof File) {
        const uploaded = await dispatch(uploadFiles([actionPayload.spousePhoto], null, true));
        spousePhotoUrl = uploaded[0]?.url || uploaded[0]?.key || uploaded[0];
      }

      // 3. Prepare the payload with uploaded photo URLs
      const {
        firstName,
        lastName,
        phone,
        address1: address,
        city,
        state: region,
        zipCode,
        providerAddedDate,
        primaryPhoto,
        spousePhoto,
        questionnaire,
        ...rest
      } = actionPayload;

      // 4. Update questionnaire with photo URLs if they were uploaded
      const updatedQuestionnaire = { ...questionnaire };
      // Note: primaryPhoto is now a top-level field, not in questionnaire
      if (spousePhotoUrl) {
        updatedQuestionnaire.spousePhoto = { url: spousePhotoUrl };
      }

      const providerPayload = {
        first_name: firstName,
        last_name: lastName,
        phone,
        address,
        city,
        state: region,
        zip_code: zipCode,
        join_date: providerAddedDate,
        primary_photo: primaryPhotoUrl, // Use primary_photo as top-level field
        questionnaire: updatedQuestionnaire,
        ...rest
      };

      console.log('providerPayload', providerPayload);
      // 5. Update the profile
      const response = await updateProfile(providerId, providerPayload);
      dispatch(updateProfileSuccess(response));
      return response;
    } catch (e) {
      dispatch(updateProfileError(storableError(e)));
      throw e;
    }
  };
};

export const loadData = () => (dispatch, getState) => {
  let userId = null;

  const state = getState();
  const currentUser = state.user?.currentUser;

  if (currentUser && currentUser.id && currentUser.id.uuid) {
    userId = currentUser.id.uuid;
  }

  if (userId) {
    dispatch(fetchExtendedDataRequest());

    return getProfileByUserId(userId)
      .then(response => {
        dispatch(fetchExtendedDataSuccess(response));
        return response;
      })
      .catch(e => {
        dispatch(fetchExtendedDataError(storableError(e)));
        throw e;
      });
  }

  console.warn("User ID not found");
  return Promise.resolve();
};
